import { useState, ReactNode } from 'react';
import { Dialog, DialogTitle, DialogContent, Button, Typography, Box, Slider, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

// Slider's `marks` prop accepts boolean | { value: number; label?: ReactNode }[].
// We re-declare it locally instead of importing the removed-in-v7 `Mark` type.
type SliderMark = { value: number; label?: ReactNode };

export interface ManualMeasurementDialogProps {
  title: string;
  imageSource: string;
  onSave: (value: number) => void;
  initialValue?: number;
  suggestedMin?: number;
  suggestedMax?: number;
  absoluteMin?: number;
  absoluteMax?: number;
  sliderMarks?: boolean | SliderMark[];
  dismissDialog?: () => void;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    '& .MuiDialog-paper': {
      minHeight: '70vh',
      minWidth: '70vw',
    },
  },
}));

// Red button for the destructive-ish "Confirm save (X)" action. Inline hover
// rule so MUI's primary contained default doesn't bleed through.
const WarningButton = styled(Button)({
  backgroundColor: '#d32f2f',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#b71c1c',
  },
});

export default function ManualMeasurementDialog({
  title,
  imageSource,
  onSave,
  initialValue = 50,
  suggestedMin,
  suggestedMax,
  sliderMarks = false,
  absoluteMin = 0,
  absoluteMax = 100,
  dismissDialog,
}: ManualMeasurementDialogProps) {
  const [value, setValue] = useState<number>(initialValue);
  // Save is disabled until the operator interacts with any control — guards
  // against an accidental tap on a freshly-opened dialog.
  const [saveButtonDisabled, setSaveButtonDisabled] = useState<boolean>(true);
  // Set after Save is clicked on an out-of-range value; footer swaps to
  // [Cancel] [Confirm save (X)]. Any further value change clears it.
  const [pendingConfirm, setPendingConfirm] = useState<boolean>(false);

  const isOutOfRange =
    suggestedMin !== undefined && suggestedMax !== undefined && (value < suggestedMin || value > suggestedMax);

  const updateValue = (next: number) => {
    setValue(Math.min(Math.max(next, absoluteMin), absoluteMax));
    setSaveButtonDisabled(false);
    setPendingConfirm(false);
  };

  const onStepperClick = (delta: number) => updateValue(value + delta);

  const handleSliderChange = (_event: unknown, next: number | number[]) => {
    updateValue(next as number);
  };

  const onSaveClick = () => {
    if (isOutOfRange) {
      setPendingConfirm(true);

      return;
    }

    onSave(value);
  };

  // Green-tinted band rendered behind the slider track, spanning the
  // suggested range. Skipped when no suggested range is configured.
  const span = absoluteMax - absoluteMin;
  const suggestedBandStyle =
    span > 0 && suggestedMin !== undefined && suggestedMax !== undefined
      ? {
          left: `${((Math.max(suggestedMin, absoluteMin) - absoluteMin) / span) * 100}%`,
          width: `${((Math.min(suggestedMax, absoluteMax) - Math.max(suggestedMin, absoluteMin)) / span) * 100}%`,
        }
      : null;

  // Border color reflects validation state. Default to no-warning gray.
  let valueCardBorderColor = '#e0e0e0';

  if (isOutOfRange && pendingConfirm) {
    valueCardBorderColor = '#d32f2f';
  } else if (isOutOfRange) {
    valueCardBorderColor = '#ed6c02';
  }

  return (
    <StyledDialog open={true} maxWidth="lg" fullWidth>
      <DialogTitle sx={sxMap.titleBar}>
        <Typography variant="h5" sx={sxMap.titleText}>
          <b>Enter {title}</b>
        </Typography>
        {dismissDialog && (
          <IconButton aria-label="close modal" onClick={dismissDialog} size="large">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent sx={sxMap.content}>
        <Box sx={sxMap.imageColumn}>
          <Box component="img" sx={sxMap.image} src={imageSource} alt="soil core reference" />
        </Box>

        <Box sx={sxMap.controlsColumn}>
          <Box sx={sxMap.metaRow}>
            {suggestedMin !== undefined && suggestedMax !== undefined ? (
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Suggested {suggestedMin} to {suggestedMax}
              </Typography>
            ) : (
              <span />
            )}
            <Typography variant="body2" color="textSecondary">
              Abs {absoluteMin}–{absoluteMax}
            </Typography>
          </Box>

          <Box sx={{ ...sxMap.valueCard, borderColor: valueCardBorderColor }}>
            <Box component="span" sx={sxMap.valueText}>
              {value}
            </Box>
            <Box component="span" sx={sxMap.valueUnit}>
              cm
            </Box>
          </Box>

          <Box sx={sxMap.stepperRow}>
            <Box sx={sxMap.stepperGroup}>
              <Button sx={sxMap.stepperBtn} variant="contained" color="primary" onClick={() => onStepperClick(-10)}>
                -10
              </Button>
              <Button sx={sxMap.stepperBtn} variant="contained" color="primary" onClick={() => onStepperClick(-1)}>
                -1
              </Button>
            </Box>
            <Box sx={sxMap.stepperGroup}>
              <Button sx={sxMap.stepperBtn} variant="contained" color="primary" onClick={() => onStepperClick(1)}>
                +1
              </Button>
              <Button sx={sxMap.stepperBtn} variant="contained" color="primary" onClick={() => onStepperClick(10)}>
                +10
              </Button>
            </Box>
          </Box>

          <Box sx={sxMap.sliderContainer}>
            {suggestedBandStyle && <Box sx={sxMap.suggestedBand} style={suggestedBandStyle} />}
            <Slider
              value={value}
              onChange={handleSliderChange}
              marks={sliderMarks}
              min={absoluteMin}
              max={absoluteMax}
            />
          </Box>

          <Box sx={sxMap.warningRow}>
            {isOutOfRange && !pendingConfirm && (
              <Typography color="error" sx={{ fontWeight: 500 }}>
                ⚠ Value {value} is outside the suggested range {suggestedMin} – {suggestedMax}
              </Typography>
            )}
            {isOutOfRange && pendingConfirm && (
              <Typography color="error" sx={{ fontWeight: 500 }}>
                ⚠ Save {value} cm outside the suggested range? This will be flagged.
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <Box sx={sxMap.footer}>
        {isOutOfRange && pendingConfirm ? (
          <>
            <Button size="large" variant="outlined" onClick={() => setPendingConfirm(false)} sx={sxMap.footerBtn}>
              Cancel
            </Button>
            <WarningButton size="large" variant="contained" onClick={() => onSave(value)} sx={sxMap.footerBtn}>
              Confirm save ({value})
            </WarningButton>
          </>
        ) : (
          <Button
            size="large"
            variant="contained"
            color="primary"
            disabled={saveButtonDisabled}
            onClick={onSaveClick}
            sx={sxMap.footerBtn}
          >
            Save
          </Button>
        )}
      </Box>
    </StyledDialog>
  );
}

// sxMap groups all of the dialog's style rules. Translates the prior
// makeStyles<theme>() lookup verbatim — breakpoints stay as @media queries
// matching theme.breakpoints.up('md') (900px) used in the original.
const sxMap = {
  titleBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 1,
  },
  titleText: {
    fontSize: '24px',
    '@media (min-width:900px)': {
      fontSize: '28px',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    '@media (min-width:900px)': {
      flexDirection: 'row',
      gap: 3,
    },
  },
  imageColumn: {
    width: '100%',
    height: 80,
    flexShrink: 0,
    overflow: 'hidden',
    '@media (min-width:900px)': {
      width: '40%',
      height: 'auto',
      minHeight: 400,
    },
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    pointerEvents: 'none',
  },
  controlsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
  metaRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 2,
    flexWrap: 'wrap',
  },
  valueCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 24px',
    minHeight: 96,
    border: '4px solid',
    borderRadius: 2, // sx multiplier: 4px × 2 = 8px, matches the original
    backgroundColor: '#fafafa',
    transition: 'border-color 120ms ease-out',
    '@media (min-width:900px)': {
      minHeight: 140,
    },
  },
  valueText: {
    fontSize: '64px',
    fontWeight: 700,
    lineHeight: 1,
    fontFamily: 'monospace',
    color: '#111',
    '@media (min-width:900px)': {
      fontSize: '88px',
    },
  },
  valueUnit: {
    fontSize: '18px',
    color: '#666',
    marginTop: 0.5,
    '@media (min-width:900px)': {
      fontSize: '24px',
    },
  },
  // Stepper row holds two button groups (-10, -1) and (+1, +10) and uses
  // space-between so each group sticks to the edge of the controls column —
  // matches the value card's left/right edges above.
  stepperRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 1,
  },
  stepperGroup: {
    display: 'flex',
    flexDirection: 'row',
    gap: 1,
  },
  stepperBtn: {
    minWidth: 56,
    minHeight: 64,
    fontSize: '18px',
    fontWeight: 700,
    '@media (min-width:900px)': {
      minWidth: 96,
      minHeight: 72,
      fontSize: '20px',
    },
  },
  sliderContainer: {
    position: 'relative',
    padding: '0 12px',
    minHeight: 48,
    display: 'flex',
    alignItems: 'center',
  },
  // Tinted band behind the slider track marking the suggested range. Sits
  // inset of the slider's 12px horizontal padding so its edges align with
  // the track's start/end.
  suggestedBand: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    height: 12,
    backgroundColor: 'rgba(76, 175, 80, 0.25)',
    border: '1px solid rgba(76, 175, 80, 0.6)',
    borderRadius: 1.5, // sx multiplier: 4px × 1.5 = 6px, matches the original
    pointerEvents: 'none',
    boxSizing: 'border-box',
  },
  warningRow: {
    minHeight: 48,
    display: 'flex',
    alignItems: 'center',
  },
  footer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 1.5,
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0',
  },
  footerBtn: {
    minWidth: 120,
    minHeight: 48,
    fontSize: '16px',
    fontWeight: 700,
    '@media (min-width:900px)': {
      minWidth: 160,
      minHeight: 56,
      fontSize: '18px',
    },
  },
} as const;
